
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Wand2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

interface TextInputProps {
  text: string;
  setText: (text: string) => void;
  onSubmit: (input: string, type: "url" | "text") => void;
  isLoading: boolean;
}

export const TextInput = ({ text, setText, onSubmit, isLoading }: TextInputProps) => {
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);

  const handleTextSubmit = async () => {
    if (!text.trim()) {
      toast.error("Please enter your recipe text");
      return;
    }
    
    if (text.trim().length < 50) {
      toast.error("Recipe text is too short. Please provide a complete recipe with ingredients and instructions.");
      return;
    }
    
    try {
      setLocalIsSubmitting(true);
      // Short timeout to ensure UI state is updated before potentially intensive operation
      await new Promise(resolve => setTimeout(resolve, 100));
      onSubmit(text, "text");
    } catch (error) {
      console.error("Error submitting text:", error);
      toast.error("Failed to process recipe. Please try again.");
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  // Use either prop loading state or local loading state
  const submitting = isLoading || localIsSubmitting;

  return (
    <div className="space-y-4 w-full">
      <Textarea
        placeholder="Paste your complete recipe here including ingredients and instructions..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="min-h-[200px] p-4 text-lg w-full"
        disabled={submitting}
      />
      <div className="text-sm text-gray-500 mb-2">
        <p>For best results:</p>
        <ul className="list-disc pl-5 mt-1">
          <li>Include the full recipe with title, ingredients list, and step-by-step instructions</li>
          <li>Include cooking temperatures and times for accurate air fryer conversion</li>
          <li>Remove any unnecessary content like stories, ads, or author information</li>
        </ul>
      </div>
      <div className="w-full">
        <Button
          type="button"
          disabled={submitting}
          onClick={handleTextSubmit}
          className="h-12 w-full bg-primary hover:bg-primary/90 text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg shadow-sm group"
        >
          {submitting ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Converting...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Wand2 className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              <span className="text-lg">Convert</span>
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};
